import type { CSSProperties } from "react";
import type { GailChartPoint } from "./answerParts";

const PALETTE = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#f97316", "#64748b"];

type Props = {
  title?: string;
  points: GailChartPoint[];
};

function legend(points: GailChartPoint[]) {
  return (
    <ul style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", margin: "12px 0 0", padding: 0, listStyle: "none", fontSize: 12 }}>
      {points.map((point, index) => (
        <li key={`${point.label}-${index}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: PALETTE[index % PALETTE.length] }} />
          <span>
            {point.label} ({point.value})
          </span>
        </li>
      ))}
    </ul>
  );
}

const figureStyle: CSSProperties = { margin: "8px 0 16px" };

export function GailPieChart({ title, points }: Props) {
  const total = points.reduce((sum, point) => sum + point.value, 0);
  if (points.length === 0 || total <= 0) return null;
  let cursor = 0;
  const stops = points.map((point, index) => {
    const start = cursor;
    cursor += (point.value / total) * 100;
    return `${PALETTE[index % PALETTE.length]} ${start}% ${cursor}%`;
  });
  return (
    <figure className="gail-chart" style={figureStyle}>
      {title ? <figcaption style={{ fontWeight: 700, textAlign: "center", marginBottom: 12 }}>{title}</figcaption> : null}
      <div
        role="img"
        aria-label={title || "Pie chart"}
        style={{ width: 180, height: 180, borderRadius: "50%", background: `conic-gradient(${stops.join(", ")})` }}
      />
      {legend(points)}
    </figure>
  );
}

export function GailBarChart({ title, points }: Props) {
  const max = Math.max(...points.map((point) => point.value), 0);
  if (points.length === 0 || max <= 0) return null;
  return (
    <figure className="gail-chart" style={figureStyle}>
      {title ? <figcaption style={{ fontWeight: 700, textAlign: "center", marginBottom: 12 }}>{title}</figcaption> : null}
      <div role="img" aria-label={title || "Bar chart"} style={{ display: "grid", gap: 8 }}>
        {points.map((point, index) => (
          <div key={`${point.label}-${index}`}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
              <span>{point.label}</span>
              <span>{point.value}</span>
            </div>
            <div style={{ height: 10, borderRadius: 999, background: "rgba(127,127,127,0.2)" }}>
              <div style={{ width: `${(point.value / max) * 100}%`, height: "100%", borderRadius: 999, background: PALETTE[index % PALETTE.length] }} />
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}
